import React, { useEffect, useState } from "react";
import { useAppDispatch } from "../../app/store";
import { selectParametros } from "../../features/Dashboard/Slices/dashboardSlice";
import { useSelector } from "react-redux";
import Button from "../../components/reusable/Button/Button";
import PageLoader from "../../components/reusable/PageLoader/PageLoader";
import { DismissibleCard } from "../../components/reusable/DismissibleCard/DismissibleCard";
import { dashboardAPI } from "../../Api/dashboardAPI";
import { ToastMessage, toastError, toastSuccess } from "../../utils/toast";
import ModalConfirmation from "../../components/reusable/ModalConfirmation/ModalConfirmation";
import { getParametros } from "../../features/Dashboard/AsyncThunks/parametros/getParametros";
import SlDrawer from "@shoelace-style/shoelace/dist/react/drawer";
import SlTextarea from "@shoelace-style/shoelace/dist/react/textarea";
import SlSwitch from "@shoelace-style/shoelace/dist/react/switch";
import SlIconButton from "@shoelace-style/shoelace/dist/react/icon-button";
import SlTooltip from "@shoelace-style/shoelace/dist/react/tooltip";
import useAuth from "../../hooks/useAuth";
import { formatearJSON, validarJSON } from "../../utils";
import { agruparParametros, validarParametro } from "./ParametrosHelper";
export enum Parametro {
  PROCESAR_NOVEDADES_ORACLE_MANUALMENTE = "PROCESAR_NOVEDADES_ORACLE_MANUALMENTE",
  DESCARTAR_NOVEDADES_ORACLE = "DESCARTAR_NOVEDADES_ORACLE",
}

function Parametros() {
  const dispatch = useAppDispatch();
  const parametros = useSelector(selectParametros);
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [showDepuracionModal, setShowDepuracionModal] = useState(false);
  const [paramToEdit, setParamToEdit] = useState<string | null>(null);
  const [newParamValue, setNewParamValue] = useState<string>("");
  const [depuracionResult, setDepuracionResult] = useState<string | null>(null);
  const [depuracionTheme, setDepuracionTheme] = useState<
    "success" | "error" | "info"
  >("info");

  // Obtener parámetros al montar
  useEffect(() => {
    if (!parametros.length) {
      setLoading(true);
      obtenerParametros();
      setLoading(false);
    }
  }, []);

  const obtenerParametros = async () => {
    await dispatch(getParametros());
  };

  const depurarParametrosObsoletos = async () => {
    setShowDepuracionModal(false);
    setLoading(true);
    try {
      const response = await dashboardAPI.depurarParametrosObsoletos();
      console.log("DEPURAR PARAMS:", response);
      toastSuccess("Parámetros obsoletos depurados correctamente");
      setDepuracionResult(
        `Parametros obsoletos depurados correctamente: ${JSON.stringify(response, null, 2)}`,
      );
      setDepuracionTheme("success");
    } catch (error) {
      toastError();
      setDepuracionResult(`Error al depurar parámetros obsoletos: ${error}`);
      setDepuracionTheme("error");
    } finally {
      setLoading(false);
    }
  };

  const ejecutarActualizacion = async (clave: string, valor: string) => {
    const resultadoValidacion = validarParametro(clave, valor);

    if (!resultadoValidacion.esValido) {
      toastError(resultadoValidacion.error || "Error de validación");
      setShowModalConfirmation(false);
      return;
    }
    let success = false;
    setLoading(true);
    try {
      await dashboardAPI.updateParametro(clave, valor);
      success = true;
      await obtenerParametros(); // Refrescar parámetros
    } catch (error) {
      toastError(ToastMessage.error);
    } finally {
      setShowModalConfirmation(false);
      setLoading(false);
      if (success) {
        toastSuccess(`Parámetro ${clave} actualizado correctamente`);
      }
      setParamToEdit(null);
      setNewParamValue("");
    }
  };

  const updateParametro = async () => {
    if (!paramToEdit) return;
    console.log("UPDATE PARAMETRO POST CONFIRM");
    await ejecutarActualizacion(paramToEdit, newParamValue);
  };

  const handleEdit = (clave: string, valor: string) => {
    setParamToEdit(clave);
    setNewParamValue(valor);
  };

  // Parámetros booleanos que requieren confirmación antes de cambiar
  // por tratarse de operaciones sensibles.
  const requiereConfirmacion = (clave: string) =>
    Object.values(Parametro).includes(clave as Parametro);

  const handleSwitchEdit = async (clave: string, valor: string) => {
    const newValue = valor === "true" ? "false" : "true";

    if (requiereConfirmacion(clave)) {
      setParamToEdit(clave);
      setNewParamValue(newValue);
      setShowModalConfirmation(true);
      return;
    } else {
      console.log("UPDATE PARAMETRO POST SWITCH");
      await ejecutarActualizacion(clave, newValue);
    }
  };

  const handleValidarJSON = (paramValue: string) => {
    const { esValido, error } = validarJSON(paramValue);
    if (esValido) {
      toastSuccess("JSON válido");
    } else {
      toastError(error);
    }
  };

  const currentParam = parametros.find((param) => param.clave === paramToEdit);

  return (
    <div className="p-6">
      {loading && <PageLoader />}
      <ModalConfirmation
        show={showModalConfirmation}
        message={`Seguro que quiere actualizar el parámetro ${paramToEdit}?`}
        onClose={() => setShowModalConfirmation(false)}
        onConfirm={updateParametro}
        title="Actualizar parámetro"
      />

      <ModalConfirmation
        show={showDepuracionModal}
        message="¿Está seguro que desea depurar los parámetros obsoletos? Esta acción no se puede deshacer."
        onClose={() => setShowDepuracionModal(false)}
        onConfirm={depurarParametrosObsoletos}
        title="Confirmar depuración"
        topBorderWarn={true}
      />

      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg col-span-3 border-2 border-cardBorder overflow-x-hidden">
          <h4 className="font-semibold text-textSecondary mb-3 text-[16px]">
            Configuración parámetros
          </h4>

          <div className="mt-2 space-y-5">
            {agruparParametros(parametros).map(
              ({ nombreGrupo, parametros: paramsDelGrupo }) => (
                <div key={nombreGrupo}>
                  <h5 className="font-semibold text-textSecondary text-[14px] mb-2 pb-1 border-b border-cardBorder">
                    {nombreGrupo}
                  </h5>
                  <div className="grid grid-cols-2 gap-2">
                    {paramsDelGrupo.map(({ clave, valor, descripcion }) => {
                      const editMode = paramToEdit === clave;
                      return (
                        <div
                          key={clave}
                          className=" flex items-center bg-white"
                        >
                          <div className="flex-grow">
                            <SlTooltip content={clave + ">> " + descripcion}>
                              <p className="font-semibold">{clave}</p>
                            </SlTooltip>
                          </div>
                          {valor === "true" || valor === "false" ? (
                            <SlSwitch
                              size="medium"
                              checked={valor === "true"}
                              onSlChange={
                                !hasPermission("parametros", "toggle_parametro")
                                  ? undefined
                                  : (e: any) => {
                                      const nuevoChecked = e.target.checked;
                                      if (
                                        nuevoChecked ===
                                        (valor === "true")
                                      )
                                        return;
                                      handleSwitchEdit(clave, valor);
                                    }
                              }
                              disabled={
                                !hasPermission("parametros", "toggle_parametro")
                              }
                            ></SlSwitch>
                          ) : (
                            <div className="px-1 w-1/3 flex items-center">
                              <p className="flex-grow line-clamp-1 text-secondary text-right">
                                {valor}
                              </p>
                              <SlTooltip content="Editar">
                                <SlIconButton
                                  className="text-secondary"
                                  onClick={() =>
                                    paramToEdit === clave
                                      ? setShowModalConfirmation(true)
                                      : handleEdit(clave, valor)
                                  }
                                  name="pencil-square"
                                  label="Editarr"
                                  disabled={
                                    !hasPermission(
                                      "parametros",
                                      "editar_parametro",
                                    )
                                  }
                                />
                              </SlTooltip>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        <div className="bg-white p-3 rounded-lg col-span-1 border-2 border-cardBorder self-start">
          <h4 className="font-semibold text-textSecondary mb-2 text-[14px]">
            Acciones
          </h4>
          <div className="space-y-3">
            <Button
              label="Refrescar parámetros"
              iconName="arrow-clockwise"
              onClick={obtenerParametros}
              disabled={!hasPermission("parametros", "refrescar_parametros")}
              outlined={true}
              theme="default"
              className="w-full"
            />
            <Button
              label="Depurar parámetros obsoletos"
              iconName="trash"
              onClick={() => {
                setShowDepuracionModal(true);
              }}
              disabled={!hasPermission("parametros", "depurar_parametros")}
              outlined={true}
              theme="red"
              className="w-full"
            />
            {/* Card de resultado de depuración */}
            {depuracionResult && (
              <div className="top-4 right-4 z-50 w-auto">
                <DismissibleCard
                  title="Resultado de operación"
                  onDismiss={() => {
                    setDepuracionResult(null);
                    setDepuracionTheme("info");
                  }}
                  theme={depuracionTheme}
                >
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-auto max-h-60">
                    {depuracionResult}
                  </pre>
                </DismissibleCard>
              </div>
            )}
          </div>
        </div>
      </div>

      <SlDrawer
        label="Editar parámetro"
        open={
          !!paramToEdit &&
          currentParam?.valor !== "true" &&
          currentParam?.valor !== "false"
        }
        onSlAfterHide={() => {
          setParamToEdit(null);
          setNewParamValue("");
        }}
      >
        {currentParam && (
          <div className="space-y-2">
            <p className="font-semibold">{currentParam.clave}</p>
            <p>{currentParam.descripcion}</p>
            <SlTextarea
              value={formatearJSON(newParamValue)}
              onSlInput={(e: any) => setNewParamValue(e.target.value)}
              className="w-full"
              size="medium"
              rows={17}
            />
            <Button
              iconName="check-circle"
              className="mx-3"
              theme="primary"
              label="Actualizar"
              onClick={() => setShowModalConfirmation(true)}
            />
          </div>
        )}
      </SlDrawer>
    </div>
  );
}

export default Parametros;
